import React, { useState, useRef, useEffect } from "react";
import { AtSign } from "lucide-react";

/**
 * Lightweight @mention input.
 * Detects an active @query at the caret and shows a dropdown of users + "all".
 * - users: [{id, name}]
 * - onSubmit?: (text, mentionedIds) => void  triggered on Enter (without shift)
 */
export default function MentionTextarea({
    value,
    onChange,
    placeholder,
    users,
    mentionAllLabel = "Everyone",
    onSubmit,
    onMentionsChange,
    rows = 2,
    testId,
}) {
    const ref = useRef(null);
    const prevMentionsRef = useRef("");
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [caret, setCaret] = useState(0);
    const [highlight, setHighlight] = useState(0);

    // Compute mentions whenever value changes
    useEffect(() => {
        if (!onMentionsChange) return;
        const ids = new Set();
        let allFlag = false;
        const re = /@([a-zA-ZÀ-ÿğüşıöçĞÜŞİÖÇ_\w]+)/g;
        let m;
        while ((m = re.exec(value))) {
            const name = m[1].toLowerCase();
            if (name === "all" || name === "herkes" || name === "alle") {
                allFlag = true;
            } else {
                const u = users.find((x) => x.name.toLowerCase().replace(/\s+/g, "") === name);
                if (u) ids.add(u.id);
            }
        }
        const list = allFlag ? users.map((u) => u.id) : Array.from(ids);
        const key = JSON.stringify(list) + ":" + allFlag;
        if (prevMentionsRef.current !== key) {
            prevMentionsRef.current = key;
            onMentionsChange(list, allFlag);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, users]);

    const handleChange = (e) => {
        const v = e.target.value;
        const pos = e.target.selectionStart || 0;
        onChange(v);
        setCaret(pos);

        // Look back from caret to find an open @
        const before = v.slice(0, pos);
        const match = before.match(/(?:^|\s)@([a-zA-ZÀ-ÿğüşıöçĞÜŞİÖÇ_\w]*)$/);
        if (match) {
            setQuery(match[1]);
            setOpen(true);
            setHighlight(0);
        } else {
            setOpen(false);
        }
    };

    const candidates = (() => {
        const q = query.toLowerCase();
        const opts = [
            { id: "__all__", name: mentionAllLabel, isAll: true },
            ...users.filter((u) => u.name.toLowerCase().includes(q)),
        ];
        if (q) return opts.filter((o) => o.isAll ? "all".startsWith(q) || mentionAllLabel.toLowerCase().includes(q) : true);
        return opts;
    })();

    const insertMention = (cand) => {
        const v = value;
        const before = v.slice(0, caret);
        const after = v.slice(caret);
        const replaced = before.replace(/@([a-zA-ZÀ-ÿğüşıöçĞÜŞİÖÇ_\w]*)$/, (cand.isAll ? "@all " : `@${cand.name.replace(/\s+/g, "")} `));
        const newVal = replaced + after;
        onChange(newVal);
        setOpen(false);
        // Place caret right after insertion
        const newCaret = replaced.length;
        requestAnimationFrame(() => {
            if (ref.current) {
                ref.current.focus();
                ref.current.setSelectionRange(newCaret, newCaret);
            }
        });
    };

    const handleKeyDown = (e) => {
        if (open) {
            if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, candidates.length - 1)); return; }
            if (e.key === "ArrowUp")   { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); return; }
            if (e.key === "Enter" || e.key === "Tab") {
                if (candidates[highlight]) { e.preventDefault(); insertMention(candidates[highlight]); return; }
            }
            if (e.key === "Escape") { setOpen(false); return; }
        }
        if (e.key === "Enter" && !e.shiftKey && onSubmit) {
            e.preventDefault();
            onSubmit();
        }
    };

    return (
        <div className="relative">
            <textarea
                ref={ref}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onSelect={(e) => setCaret(e.target.selectionStart || 0)}
                placeholder={placeholder}
                rows={rows}
                data-testid={testId}
                className="w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-900/10 focus:border-neutral-400 resize-none"
            />
            {open && candidates.length > 0 && (
                <div
                    data-testid="mention-dropdown"
                    className="absolute z-50 left-0 right-0 mt-1 max-h-48 overflow-y-auto rounded-md border border-neutral-200 bg-white shadow-lg"
                >
                    {candidates.map((c, i) => (
                        <button
                            key={c.id}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); insertMention(c); }}
                            data-testid={c.isAll ? "mention-all" : `mention-${c.id}`}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${i === highlight ? "bg-neutral-100" : "hover:bg-neutral-50"}`}
                        >
                            <AtSign className="w-3.5 h-3.5 text-neutral-500" />
                            <span className={c.isAll ? "font-semibold" : ""}>{c.name}</span>
                            {c.isAll && <span className="ml-auto text-[10px] text-neutral-500 uppercase tracking-wider">all</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
